import { Injectable } from '@angular/core';
import { FontTypes } from '@longnecktech/splash-commons-fe/dist/utils/theme.util';
import { BehaviorSubject, catchError, EMPTY, map, take } from 'rxjs';
import {
  injectCSSVariablesIntoDOM,
  ConfigValue,
} from '@longnecktech/splash-commons-fe';
import { JackpotTheme } from '@shared/types/jackpot-theme';
import { TermsAndConditionsResponse } from '@shared/types/terms-and-conditions';
import { HttpClient } from '@angular/common/http';

type JackpotThemeResponse = { theme: JackpotTheme };

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private _isMobile = new BehaviorSubject<boolean>(false);
  readonly isMobile$ = this._isMobile.asObservable();

  private _theme = new BehaviorSubject<JackpotTheme | null>(null);
  readonly theme$ = this._theme.asObservable();

  private _themeLoaded = new BehaviorSubject<boolean>(false);
  themeLoaded$ = this._themeLoaded.asObservable();

  private _termsAndConditions = new BehaviorSubject<TermsAndConditionsResponse | null>(
    null,
  );
  termsAndConditions$ = this._termsAndConditions.asObservable();

  private _termsAndConditionsLoaded = new BehaviorSubject<boolean>(false);
  termsAndConditionsLoaded$ = this._termsAndConditionsLoaded.asObservable();

  constructor(private http: HttpClient) {}

  private injectThemeIntoDOM(theme: JackpotTheme) {
    const styleEl = document.createElement('style');

    styleEl.textContent = injectCSSVariablesIntoDOM(theme, {
      defaultSecondaryCtaButtonColors: {
        '--z-cta-buttons-outline-background-color': 1,
        '--color-accent-1': 2,
      },
      defaultFonts: {
        [FontTypes.Regular]: '/assets/fonts/Roboto/static/Roboto-Regular.ttf',
        [FontTypes.Bold]: '/assets/fonts/Roboto/static/Roboto-Bold.ttf',
        [FontTypes.Medium]: '/assets/fonts/Roboto/static/Roboto-Medium.ttf',
        [FontTypes.Headlines]:
          '/assets/fonts/Inter/static/Inter_18pt-Regular.ttf',
      },
      defaultCtaBorderRadius: 28,
    });

    document.head.appendChild(styleEl);
  }

  setTheme(themeUrl: string | null | undefined): void {
    // prevent theme from being updated after it has been set once
    if (this._themeLoaded.value) {
      return;
    }

    const applyTheme = (theme: JackpotTheme) => {
      this.injectThemeIntoDOM(theme);
      this._theme.next(theme);
      this._themeLoaded.next(true);
    };

    const loadFallbackTheme = () => {
      this.http
        .get<JackpotThemeResponse>('assets/fallback/themes/splash-theme.json')
        .pipe(take(1))
        .subscribe({
          next: (theme) => applyTheme(theme.theme),
          error: (err) => console.error('Failed to load fallback theme: ', err),
        });
    };

    if (!themeUrl) {
      console.error('themeUrl missing. Loading fallback theme');
      loadFallbackTheme();
      return;
    }

    this.http
      .get<JackpotThemeResponse>(themeUrl)
      .pipe(
        take(1),
        map((theme) => {
          if (!theme || !theme.theme || Object.keys(theme).length === 0) {
            throw new Error('Invalid theme: ' + JSON.stringify(theme));
          }
          return theme.theme;
        }),
        catchError((err) => {
          console.error(
            `Failed to load theme. Using fallback theme. Error: ${err.message}`,
          );
          loadFallbackTheme();
          return EMPTY;
        }),
      )
      .subscribe((theme) => {
        applyTheme(theme);
      });
  }

  setTermsAndConditions(termsAndConditionsUrl: string | null | undefined): void {
    if (this._termsAndConditionsLoaded.value) {
      return;
    }

    const applyTermsAndConditions = (
      termsAndConditions: TermsAndConditionsResponse,
    ) => {
      this._termsAndConditions.next(termsAndConditions);
      this._termsAndConditionsLoaded.next(true);
    };

    const loadFallbackTermsAndConditions = () => {
      this.http
        .get<TermsAndConditionsResponse>(
          'assets/fallback/terms-and-conditions/splash-terms-and-conditions.json',
        )
        .pipe(take(1))
        .subscribe({
          next: (response) => applyTermsAndConditions(response),
          error: (err) =>
            console.error('Failed to load fallback terms and conditions: ', err),
        });
    };

    if (!termsAndConditionsUrl) {
      console.error(
        'termsAndConditionsUrl missing. Loading fallback terms and conditions',
      );
      loadFallbackTermsAndConditions();
      return;
    }

    this.http
      .get<TermsAndConditionsResponse>(termsAndConditionsUrl)
      .pipe(
        take(1),
        map((response) => {
          if (
            !response ||
            typeof response.text !== 'string' ||
            Object.keys(response).length === 0
          ) {
            throw new Error(
              'Invalid terms and conditions: ' + JSON.stringify(response),
            );
          }
          return response;
        }),
        catchError((err: Error) => {
          console.error(
            `Failed to load terms and conditions. Using fallback. Error: ${err.message}`,
          );
          loadFallbackTermsAndConditions();
          return EMPTY;
        }),
      )
      .subscribe((termsAndConditions) => {
        applyTermsAndConditions(termsAndConditions);
      });
  }

  getLabels(): ConfigValue[] {
    return this._theme.value?.labels || [];
  }

  updateIsMobile(isMobile: boolean): void {
    this._isMobile.next(isMobile);
  }
}
